import { useState, type FormEvent } from "react";
import { api, message, type Body, type Person, type SeatRole } from "../api";

/** Suggestions only: any short title the client uses is fine. */
const HONORIFIC_SUGGESTIONS = ["Ms.", "Mr.", "Mrs.", "Mx.", "Dr.", "Chief", "Director", "Commander", "Captain"];

/**
 * What goes before the surname after first mention: "Ms." for "Ms. Sabin",
 * or a title -- "Chief", "Director" -- for "Chief Sabin". Free text with
 * the common forms suggested.
 */
export function HonorificInput(props: {
  id?: string;
  value: string;
  ariaLabel?: string;
  onChange: (v: string) => void;
  /** Called on blur or Enter, for inputs that save as they go. */
  onCommit?: (v: string) => void;
}) {
  return (
    <>
      <input
        id={props.id}
        className="honorific-input"
        list="honorific-suggestions"
        value={props.value}
        maxLength={40}
        placeholder="Not set"
        aria-label={props.ariaLabel}
        onChange={(e) => props.onChange(e.target.value)}
        onBlur={(e) => props.onCommit?.(e.target.value.trim())}
        onKeyDown={(e) => { if (e.key === "Enter" && props.onCommit) { e.preventDefault(); e.currentTarget.blur(); } }}
      />
      <datalist id="honorific-suggestions">
        {HONORIFIC_SUGGESTIONS.map((h) => <option key={h} value={h} />)}
      </datalist>
    </>
  );
}

/** What Mr./Ms./Mrs. imply; any other honorific implies nothing. */
function impliedPronoun(honorific: string | null): "he" | "she" | null {
  const h = (honorific ?? "").trim().toLowerCase().replace(/\.$/, "");
  return h === "mr" ? "he" : h === "ms" || h === "mrs" || h === "miss" ? "she" : null;
}

/**
 * Whether the minutes may say "he" or "she" for someone after naming them.
 * Left on automatic, Mr./Ms./Mrs. settle it; otherwise the name is repeated.
 */
export function PronounSelect(props: {
  id?: string;
  value: "he" | "she" | null;
  honorific: string | null;
  ariaLabel?: string;
  onChange: (v: "he" | "she" | null) => void;
}) {
  const implied = impliedPronoun(props.honorific);
  return (
    <select id={props.id} aria-label={props.ariaLabel} value={props.value ?? ""}
            onChange={(e) => props.onChange(e.target.value === "" ? null : (e.target.value as "he" | "she"))}>
      <option value="">{implied ? `${implied} (from ${props.honorific})` : "none — repeat the name"}</option>
      <option value="he">he</option>
      <option value="she">she</option>
    </select>
  );
}

const ROLE_LABEL: Record<SeatRole, string> = {
  presiding: "Presiding",
  pro_tem: "Pro tem",
  member: "Member",
  staff_to_body: "Staff to the body",
};

interface SeatDraft { bodyId: string; role: SeatRole; titleOverride: string }

/** The title a seat prints in the minutes, exactly as the drafter resolves it. */
function seatPreview(s: SeatDraft, bodies: Body[]): string {
  const b = bodies.find((x) => x.id === s.bodyId);
  if (!b) return "";
  if (s.titleOverride.trim()) return s.titleOverride.trim();
  if (s.role === "presiding") return b.presidingTitle;
  if (s.role === "pro_tem") return b.proTemTitle ?? b.presidingTitle;
  if (s.role === "staff_to_body") return "needs a title";
  return b.memberTitle;
}

/**
 * Adds or edits one person: name, standing, how the minutes address them,
 * and the seats they hold. Saving writes the person, then their seats.
 */
export function PersonEditor(props: {
  clientId: string;
  person: Person | null;
  bodies: Body[];
  onDone: () => void;
  onCancel: () => void;
}) {
  const p = props.person;
  const [firstName, setFirstName] = useState(p?.firstName ?? "");
  const [lastName, setLastName] = useState(p?.lastName ?? "");
  const [kind, setKind] = useState<Person["kind"]>(p?.kind ?? "official");
  const [staffTitle, setStaffTitle] = useState(p?.staffTitle ?? "");
  const [honorific, setHonorific] = useState(p?.honorificPrefix ?? "");
  const [pronoun, setPronoun] = useState<"he" | "she" | null>(p?.pronoun ?? null);
  const [active, setActive] = useState(p?.active ?? true);
  const [seats, setSeats] = useState<SeatDraft[]>(
    (p?.seats ?? []).map((s) => ({ bodyId: s.bodyId, role: s.role, titleOverride: s.titleOverride ?? "" })));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const free = props.bodies.filter((b) => !seats.some((s) => s.bodyId === b.id));
  const setSeat = (i: number, patch: Partial<SeatDraft>) =>
    setSeats((all) => all.map((s, j) => (j === i ? { ...s, ...patch } : s)));

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null);
    const fields = {
      firstName, lastName, kind,
      staffTitle: staffTitle.trim() || null,
      honorificPrefix: honorific.trim() || null,
      pronoun,
      active,
    };
    try {
      let id = p?.id;
      if (id) await api.patch(`/api/people/${id}`, fields);
      else id = (await api.post<{ id: string }>(`/api/clients/${props.clientId}/people`, fields)).id;
      await api.put(`/api/people/${id}/seats`, {
        seats: seats.map((s) => ({ bodyId: s.bodyId, role: s.role, titleOverride: s.titleOverride.trim() || null })),
      });
      props.onDone();
    } catch (err) { setError(message(err)); }
    finally { setBusy(false); }
  }

  return (
    <form className="card card--form roster-editor" onSubmit={submit}>
      <h2>{p ? `Edit ${p.firstName} ${p.lastName}` : "Add a person"}</h2>
      {error && <div className="alert" role="alert">{error}</div>}

      <div className="field-row">
        <div className="field">
          <label htmlFor="pfirst">First name</label>
          <input id="pfirst" required value={firstName} onChange={(e) => setFirstName(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="plast">Last name</label>
          <input id="plast" required value={lastName} onChange={(e) => setLastName(e.target.value)} />
        </div>
      </div>

      <div className="field-row">
        <div className="field">
          <label htmlFor="pkind">Standing</label>
          <select id="pkind" value={kind} onChange={(e) => setKind(e.target.value as Person["kind"])}>
            <option value="official">Official — elected or appointed</option>
            <option value="staff">Staff</option>
            <option value="other">Other — e.g. a regular presenter</option>
          </select>
        </div>
        <div className="field">
          <label htmlFor="phon">Honorific or title</label>
          <HonorificInput id="phon" value={honorific} onChange={setHonorific} />
          <p className="hint">
            Used before the surname after first mention: "Ms." → Ms. {lastName || "Sabin"}; "Chief" → Chief {lastName || "Sabin"}.
            Never guessed from a name — until it is set, the minutes leave a blank. Officials use their seat title.
          </p>
        </div>
      </div>

      <div className="field">
        <label htmlFor="ppron">Pronoun in the minutes</label>
        <PronounSelect id="ppron" value={pronoun} honorific={honorific.trim() || null} onChange={setPronoun} />
        <p className="hint">
          Lets later sentences say "He stated…" or "She asked…" instead of repeating the name. Never guessed:
          with none set, the minutes repeat the name.
        </p>
      </div>

      {kind !== "official" && (
        <div className="field">
          <label htmlFor="ptitle">Title</label>
          <input id="ptitle" value={staffTitle} placeholder="Director of Public Works"
                 onChange={(e) => setStaffTitle(e.target.value)} />
          <p className="hint">As it should read on first mention in the minutes.</p>
        </div>
      )}

      <div className="field">
        <label>Seats</label>
        {seats.length === 0 && <p className="hint">No seats. Officials need one to be addressed by title.</p>}
        {seats.map((s, i) => (
          <div key={s.bodyId} className="seat-row">
            <select aria-label="Body" value={s.bodyId} onChange={(e) => setSeat(i, { bodyId: e.target.value })}>
              {props.bodies.filter((b) => b.id === s.bodyId || free.includes(b)).map((b) => (
                <option key={b.id} value={b.id}>{b.abbrev} — {b.name}</option>
              ))}
            </select>
            <select aria-label="Role" value={s.role} onChange={(e) => setSeat(i, { role: e.target.value as SeatRole })}>
              {(Object.keys(ROLE_LABEL) as SeatRole[]).map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
            </select>
            <input aria-label="Title override" value={s.titleOverride}
                   placeholder={s.role === "staff_to_body" ? "Title (required)" : "Title override (optional)"}
                   onChange={(e) => setSeat(i, { titleOverride: e.target.value })} />
            <span className="muted small">→ {seatPreview(s, props.bodies)}</span>
            <button type="button" className="linkbtn" onClick={() => setSeats((all) => all.filter((_, j) => j !== i))}>
              Remove
            </button>
          </div>
        ))}
        {free.length > 0 && (
          <button type="button" className="btn btn--inline btn--ghost btn--small"
                  onClick={() => setSeats((all) => [...all, { bodyId: free[0]!.id, role: "member", titleOverride: "" }])}>
            Add a seat
          </button>
        )}
      </div>

      {p && (
        <label className="check">
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
          Active — offered when naming speakers. Untick when someone leaves; they stay
          named in past meetings.
        </label>
      )}

      <div className="actions">
        <button className="btn btn--inline" type="submit" disabled={busy}>{busy ? "Saving…" : "Save"}</button>
        <button className="linkbtn" type="button" onClick={props.onCancel} disabled={busy}>Cancel</button>
      </div>
    </form>
  );
}

/** Adds or edits a body: the titles it gives the people seated on it. */
export function BodyEditor(props: {
  clientId: string;
  body: Body | null;
  onDone: () => void;
  onCancel: () => void;
}) {
  const b = props.body;
  const [name, setName] = useState(b?.name ?? "");
  const [abbrev, setAbbrev] = useState(b?.abbrev ?? "");
  const [presidingTitle, setPresiding] = useState(b?.presidingTitle ?? "");
  const [memberTitle, setMember] = useState(b?.memberTitle ?? "");
  const [proTemTitle, setProTem] = useState(b?.proTemTitle ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null);
    const fields = { name, abbrev, presidingTitle, memberTitle, proTemTitle: proTemTitle.trim() || null };
    try {
      if (b) await api.patch(`/api/bodies/${b.id}`, fields);
      else await api.post(`/api/clients/${props.clientId}/bodies`, fields);
      props.onDone();
    } catch (err) { setError(message(err)); }
    finally { setBusy(false); }
  }

  return (
    <form className="card card--form roster-editor" onSubmit={submit}>
      <h2>{b ? `Edit ${b.name}` : "Add a body"}</h2>
      {error && <div className="alert" role="alert">{error}</div>}
      <div className="field-row">
        <div className="field">
          <label htmlFor="bname">Name</label>
          <input id="bname" required value={name} placeholder="Economic Development Authority"
                 onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="babbrev">Abbreviation</label>
          <input id="babbrev" required value={abbrev} placeholder="EDA" pattern="[A-Za-z0-9.]+"
                 onChange={(e) => setAbbrev(e.target.value)} />
        </div>
      </div>
      <div className="field-row">
        <div className="field">
          <label htmlFor="bpres">Presiding title</label>
          <input id="bpres" required value={presidingTitle} placeholder="President"
                 onChange={(e) => setPresiding(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="bmem">Member title</label>
          <input id="bmem" required value={memberTitle} placeholder="Commissioner"
                 onChange={(e) => setMember(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="bpro">Pro tem title</label>
          <input id="bpro" value={proTemTitle} placeholder="Vice President (optional)"
                 onChange={(e) => setProTem(e.target.value)} />
        </div>
      </div>
      <div className="actions">
        <button className="btn btn--inline" type="submit" disabled={busy}>{busy ? "Saving…" : "Save"}</button>
        <button className="linkbtn" type="button" onClick={props.onCancel} disabled={busy}>Cancel</button>
      </div>
    </form>
  );
}
